/**
 * MaintenanceGate — site-wide "we'll be back shortly" holding page.
 *
 * Polls GET /api/site/status. When the admin has turned maintenance mode ON, the
 * whole storefront is replaced with a branded holding page for ordinary visitors.
 *
 * Admins are NEVER locked out: a logged-in admin sees the real site plus a thin
 * banner reminding them the public is currently seeing the holding page. The
 * /login route also stays reachable so an admin can sign in to lift the pause.
 *
 * Fails open — if the status check errors, the site renders normally.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthCtx } from '../App';
import api from '../utils/api';
import Logo from './Logo';

// Time the ball spends hopping from one letter to the next.
const HOP_MS = 360;

/**
 * "Follow the bouncing ball" heading. Renders each character as its own span,
 * measures where each one sits, and animates a green ball that hops from letter
 * to letter — arcing up between them and tapping down on each. The ball colour
 * is the same tea-green used across the UI. Re-measures on resize so it keeps
 * tracking the letters if the heading reflows (e.g. wraps on small screens).
 */
function BouncingHeading({ text }) {
  const wrapRef = useRef(null);
  const letterRefs = useRef([]);
  const chars = Array.from(text);
  const [spots, setSpots] = useState([]); // landing point per letter, rel. to wrapper
  const [i, setI] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      const els = letterRefs.current;
      // Only measure once every letter is mounted — a ResizeObserver callback can
      // fire mid-render while some refs are transiently detached, and a partial,
      // mis-indexed read would make the ball jump to the wrong place (or freeze).
      if (els.length < chars.length || els.slice(0, chars.length).some((el) => !el)) return;
      const next = [];
      for (let k = 0; k < chars.length; k++) {
        const el = els[k];
        next.push({ x: el.offsetLeft + el.offsetWidth / 2, y: el.offsetTop });
      }
      setSpots(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [text, chars.length]);

  // Arm the hop interval exactly once. Reading the letter count through a ref
  // (rather than depending on spots.length) keeps the timer from being cleared
  // and re-armed on every re-measure, which would stop it from ever firing.
  const lenRef = useRef(0);
  useEffect(() => { lenRef.current = spots.length; }, [spots.length]);
  useEffect(() => {
    const id = setInterval(() => {
      if (lenRef.current > 0) setI((prev) => (prev + 1) % lenRef.current);
    }, HOP_MS);
    return () => clearInterval(id);
  }, []);

  const target = spots[i];

  return (
    <span ref={wrapRef} className="relative inline-block">
      <style>{`@keyframes tt-ball-hop {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-18px); }
      }`}</style>
      {chars.map((ch, idx) => (
        <span
          key={idx}
          ref={(el) => { letterRefs.current[idx] = el; }}
          style={{ whiteSpace: 'pre' }}
          className={`transition-colors duration-200 ${idx === i ? 'text-tea-600 dark:text-tea-400' : ''}`}
        >
          {ch}
        </span>
      ))}
      {target ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0"
          style={{ transform: `translate(${target.x}px, ${target.y}px)`, transition: `transform ${HOP_MS}ms linear` }}
        >
          <span
            className="block h-3 w-3 rounded-full bg-tea-600 shadow-md shadow-tea-600/40 dark:bg-tea-500"
            style={{ marginLeft: '-0.375rem', marginTop: '-0.9rem', animation: `tt-ball-hop ${HOP_MS}ms ease-in-out infinite` }}
          />
        </span>
      ) : null}
    </span>
  );
}

export default function MaintenanceGate() {
  const [status, setStatus] = useState({ maintenanceMode: false });
  const { user } = useAuthCtx();
  const location = useLocation();

  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const { data } = await api.get('/site/status');
        if (active) setStatus(data || { maintenanceMode: false });
      } catch {
        if (active) setStatus({ maintenanceMode: false }); // fail open
      }
    };
    check();
    // Re-check periodically so a freshly-lifted pause clears without a manual
    // reload, and so a visitor sitting on a tab eventually sees the holding page.
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, 30000);
    return () => { active = false; clearInterval(id); };
  }, []);

  if (!status.maintenanceMode) return null;

  const isAdmin = user?.role === 'admin';

  // Admins keep full access — just remind them the public is paused out.
  if (isAdmin) {
    return (
      <div className="fixed bottom-3 left-1/2 z-[9998] -translate-x-1/2 flex items-center gap-3 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm shadow-lg">
        <span aria-hidden="true">🚧</span>
        <span className="font-medium text-amber-900">
          Maintenance mode is <strong>ON</strong> — visitors see the holding page.
        </span>
        <Link to="/admin/maintenance" className="font-semibold text-amber-900 underline underline-offset-2">
          Manage
        </Link>
      </div>
    );
  }

  // Keep the login route reachable so an admin can sign in and lift the pause.
  if (location.pathname.startsWith('/login')) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-gradient-to-b from-tea-50 to-white px-6 text-center dark:from-slate-900 dark:to-slate-950">
      <div className="mb-8 flex items-center">
        <Logo stacked className="text-4xl" />
      </div>

      <div className="w-full max-w-lg rounded-2xl border border-tea-100 bg-white/80 p-8 shadow-xl backdrop-blur dark:border-slate-800 dark:bg-slate-900/70 sm:p-10">
        <h1 className="mb-3 pt-7 text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
          <BouncingHeading text={status.heading || "We'll be back shortly"} />
        </h1>
        <p className="mx-auto max-w-md whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-slate-300 sm:text-base">
          {status.message ||
            'Aloha Amino is taking a short break for scheduled maintenance. We\'ll be open again very soon — thanks for your patience.'}
        </p>
        {status.eta ? (
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-tea-50 px-4 py-1.5 text-sm font-medium text-tea-700 dark:bg-slate-800 dark:text-tea-300">
            <span aria-hidden="true">⏳</span>
            Expected back: {status.eta}
          </div>
        ) : null}
      </div>

      <p className="mt-8 text-xs text-gray-400 dark:text-slate-500">
        © {new Date().getFullYear()} Aloha Amino · For laboratory research use only
      </p>

      {/* Discreet entry point so an admin can sign in and lift the pause without
          having to remember the /login URL. Ordinary visitors can click it too,
          but only an admin account can actually clear maintenance mode. */}
      <Link
        to="/login"
        className="mt-3 text-xs font-medium text-gray-400 underline underline-offset-2 transition-colors hover:text-tea-600 dark:text-slate-500 dark:hover:text-tea-400"
      >
        Admin sign in
      </Link>
    </div>
  );
}
